import React, { useMemo, useState } from 'react'
import { Terminal, Filter } from 'lucide-react'
import type { LogEntry } from '../lib/api'

interface LogsPanelProps {
  className?: string
  logs: LogEntry[]
}

const getLevelConfig = (level: string) => {
  if (level === 'success') return { color: 'text-[#10b981]', icon: '✓' }
  if (level === 'warning') return { color: 'text-[#f59e0b]', icon: '⚠' }
  if (level === 'error') return { color: 'text-[#ef4444]', icon: '✗' }
  return { color: 'text-[#06b6d4]', icon: 'ℹ' }
}

export function LogsPanel({ className = '', logs }: LogsPanelProps) {
  const [source, setSource] = useState('all')
  const sources = useMemo(() => ['all', ...Array.from(new Set(logs.map((l) => l.source)))], [logs])
  const filtered = source === 'all' ? logs : logs.filter((l) => l.source === source)

  return (
    <div id="activity-log" className={`${className} bg-[#111827] rounded-xl border border-[#374151] overflow-hidden flex flex-col`}>
      <div className="px-4 py-3 border-b border-[#374151] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-[#06b6d4]" />
          <h3 className="font-semibold">ACTIVITY LOG</h3>
          <span className="text-xs text-[#6b7280] font-mono">{filtered.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#6b7280]" />
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="bg-[#0a0f1a] border border-[#374151] rounded text-xs text-[#f9fafb] px-2 py-1"
          >
            {sources.map((s) => (
              <option key={s} value={s}>{s === 'all' ? 'All Sources' : s}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-2 font-mono text-sm">
        {filtered.map((log, i) => {
          const config = getLevelConfig(log.level)
          return (
            <div key={`${log.ts}-${i}`} className="flex items-start gap-3 hover:bg-[#1f2937] rounded px-2 py-1 transition-colors">
              <span className="text-[#6b7280] whitespace-nowrap">{log.time}</span>
              <span className={config.color}>{config.icon}</span>
              <span className="text-[#f9fafb] flex-1">{log.message}</span>
              <span className="text-xs text-[#6b7280] px-2 py-0.5 bg-[#0a0f1a] rounded">{log.source}</span>
            </div>
          )
        })}
        {filtered.length === 0 && <div className="text-[#6b7280] text-sm">No log lines yet.</div>}
      </div>
    </div>
  )
}
